import React, { useId } from 'react';
import './ExternalPaymentFields.css';
import { renderLinkifiedText } from '../../../utils/linkify';
import {
  buildExternalPaymentInstructions,
  EXTERNAL_PAYMENT_METHODS,
} from '../../../utils/externalPaymentInstructions';

const METHOD_HELP = {
  bank_transfer: 'Pay into the club bank account.',
  direct_debit: 'Set up a Direct Debit with the club.',
  payment_link: 'Pay using the club’s own payment page.',
  cash: 'Arrange a cash payment with the club.',
  cheque: 'Arrange a cheque payment with the club.',
  other: 'Agree another way to pay with the club.',
};

export default function ExternalPaymentFields({ value, onChange, disabled }) {
  const id = useId();
  const update = (key, nextValue) => onChange({ ...value, [key]: nextValue });
  const methods = value.methods || [];
  const toggleMethod = (method, checked) => update('methods', checked
    ? [...methods, method]
    : methods.filter(selected => selected !== method));
  const field = (key, label, placeholder, multiline = false, required = false) => (
    <div className="form-group">
      <label htmlFor={`${id}-${key}`}>{label}</label>
      {multiline ? <textarea id={`${id}-${key}`} className="form-input" rows={3}
        value={value[key] || ''} onChange={event => update(key, event.target.value)}
        placeholder={placeholder} disabled={disabled} required={required} maxLength={1000} />
        : <input id={`${id}-${key}`} className="form-input" type={['paymentLink', 'directDebitLink'].includes(key) ? 'url' : 'text'}
          value={value[key] || ''} onChange={event => update(key, event.target.value)}
          placeholder={placeholder} disabled={disabled} required={required} maxLength={300} />}
    </div>
  );
  const preview = buildExternalPaymentInstructions(value);
  const methodFields = {
    bank_transfer: <>{field('accountName', 'Club account name', 'Name on the club bank account', false, true)}{field('bankDetails', 'Club bank details', 'Sort code and account number, or IBAN and BIC', true, true)}</>,
    payment_link: field('paymentLink', 'Club payment link', 'https://', false, true),
    direct_debit: field('directDebitLink', 'Direct Debit setup link (optional)', 'https://'),
  };
  return <div className="external-payment-fields">
    <fieldset className="external-payment-methods" disabled={disabled}>
      <legend>How should members pay?</legend>
      <p className="text-muted" id={`${id}-method-help`}>Select every option your club accepts. Unticking an option keeps its details for later and removes it from member instructions.</p>
      <div className="external-payment-methods__options">
        {Object.entries(EXTERNAL_PAYMENT_METHODS).map(([key, label]) => <div key={key} className={`external-payment-method${methods.includes(key) ? ' is-selected' : ''}`}>
          <label className="external-payment-method__toggle">
            <input type="checkbox" checked={methods.includes(key)} aria-labelledby={`${id}-${key}-label`} aria-describedby={`${id}-method-help ${id}-${key}-help`}
              onChange={event => toggleMethod(key, event.target.checked)} />
            <span><strong id={`${id}-${key}-label`}>{label}</strong><span id={`${id}-${key}-help`} className="external-payment-method__help">{METHOD_HELP[key]}</span></span>
          </label>
          {methods.includes(key) && methodFields[key] && <div className="external-payment-method__details">{methodFields[key]}</div>}
        </div>)}
      </div>
    </fieldset>
    <details className="external-payment-shared">
      <summary className="form-subheading">Shared payment instructions</summary>
      <p className="text-muted">These details apply to all selected options. For cash, cheques or other arrangements, include how to pay or who to contact.</p>
      <div className="external-payment-shared__grid">
        {field('reference', 'Payment reference (optional)', 'Member name and membership year')}
        {field('timing', 'When should members pay? (optional)', 'After submitting your application')}
      </div>
      {field('contact', 'Who can help with payment? (optional)', 'Club treasurer email or phone number')}
      {field('notes', 'Additional payment instructions', 'How to arrange payment or how the club confirms receipt.', true)}
      <details className="external-payment-guidance"><summary>What to include in payment instructions</summary><p>Use club payment details, such as the account name, sort code, account number or IBAN. These instructions are visible to people joining the club. Leave out card numbers, security codes, passwords and individual members’ financial details.</p></details>
    </details>
    <details className="external-payment-preview">
      <summary className="external-payment-preview__heading form-subheading"><span>What members will see</span><small>{preview.length}/1000 characters</small></summary>
      <p>{preview ? renderLinkifiedText(preview) : 'Select at least one payment option to create your instructions.'}</p>
    </details>
  </div>;
}
